import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import api from '../../api';
import { Reorder, useDragControls } from 'framer-motion';
import {
  DASHBOARD_SECTIONS,
  DASHBOARD_CHARTS,
  CHART_SECTION_LABELS,
  DEFAULT_DASHBOARD_PREFERENCES,
  applyDashboardPreset,
  normalizeOrder,
  normalizeChartOrder,
} from '../../utils/dashboardSections';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import { 
  FaUser, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaFilePdf, 
  FaEdit, 
  FaSave, 
  FaTimes,
  FaKey,
  FaCheckCircle,
  FaEnvelope,
  FaWhatsapp
} from 'react-icons/fa';

// One draggable dashboard-section row. Drag is limited to the handle so the
// width/visibility buttons stay clickable.
function DashboardSectionRow({ sectionKey, label, subLabel, visible, width, darkMode, onToggleVisible, onSetWidth }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={sectionKey}
      dragListener={false}
      dragControls={controls}
      className={`flex items-center gap-3 p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}
    >
      <span
        onPointerDown={(e) => controls.start(e)}
        className={`cursor-grab select-none touch-none ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
        title="Drag to reorder"
      >
        ⠿
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm font-medium ${visible ? (darkMode ? 'text-gray-100' : 'text-gray-800') : (darkMode ? 'text-gray-500 line-through' : 'text-gray-400 line-through')}`}>
          {label}
        </span>
        {subLabel && (
          <span className={`block text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{subLabel}</span>
        )}
      </span>
      <div className={`flex rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
        <button
          type="button"
          onClick={() => onSetWidth(sectionKey, 'full')}
          className={`px-2 py-1 text-xs ${width === 'full' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          Full
        </button>
        <button
          type="button"
          onClick={() => onSetWidth(sectionKey, 'half')}
          className={`px-2 py-1 text-xs ${width === 'half' ? 'bg-indigo-600 text-white' : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          Half
        </button>
      </div>
      <button
        type="button"
        onClick={() => onToggleVisible(sectionKey)}
        title={visible ? 'Hide section' : 'Show section'}
        className={`px-2 py-1 text-xs rounded ${visible ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700') : (darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500')}`}
      >
        {visible ? 'Visible' : 'Hidden'}
      </button>
    </Reorder.Item>
  );
}

function Profile() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [editableUntil, setEditableUntil] = useState(null);
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState(() => ({
    hidden: [],
    order: [...DEFAULT_DASHBOARD_PREFERENCES.order],
    layout: {},
    ui: { ...DEFAULT_DASHBOARD_PREFERENCES.ui },
    preset: 'custom',
    charts: { hidden: [], order: [...DEFAULT_DASHBOARD_PREFERENCES.charts.order], layout: {} },
  }));
  const [savingDashboard, setSavingDashboard] = useState(false);

  // WhatsApp state
  const [waConnected, setWaConnected] = useState(false);
  const [waPhone, setWaPhone] = useState(null);
  const [waQr, setWaQr] = useState(null);
  const [waConnecting, setWaConnecting] = useState(false);
  const [waPolling, setWaPolling] = useState(false);
  const [sentMessages, setSentMessages] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [sentPage, setSentPage] = useState(1);
  const [sentPagination, setSentPagination] = useState({ current: 1, total: 1, count: 0 });

  // Sync dark mode with localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchEmployeeData();
  }, [user]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/${user.user._id}`
      );
      
      const empData = response.data;
      setEmployee(empData);
      const dp = empData.dashboardPreferences || {};
      setDashboardConfig({
        hidden: dp.hidden || [],
        order: normalizeOrder(dp.order),
        layout: dp.layout || {},
        ui: { density: dp.ui?.density || 'comfortable', accent: dp.ui?.accent || '' },
        preset: dp.preset || 'custom',
        charts: {
          hidden: dp.charts?.hidden || [],
          order: normalizeChartOrder(dp.charts?.order),
          layout: dp.charts?.layout || {},
        },
      });
      setFormData({
        name: empData.name || '',
        gender: empData.gender || '',
        dateOfBirth: empData.dateOfBirth ? empData.dateOfBirth.split('T')[0] : '',
        dateOfJoining: empData.dateOfJoining ? empData.dateOfJoining.split('T')[0] : '',
        aadharNo: empData.aadharNo || '',
        panNo: empData.panNo || '',
        postalAddress: empData.postalAddress || '',
        fatherName: empData.fatherName || '',
        motherName: empData.motherName || '',
        spouseName: empData.spouseName || '',
        phoneNumbers: empData.phoneNumbers || [],
        children: empData.children || [],
        accountName: empData.accountName || '',
        accountNo: empData.accountNo || '',
        bankName: empData.bankName || '',
        ifsc: empData.ifsc || '',
        pfNo: empData.pfNo || '',
        esicNo: empData.esicNo || '',
      });

      // Check if profile is editable (within 7 days of creation OR profileEditableUntil is set)
      const now = new Date();
      let editable = false;
      let editDeadline = null;

      if (empData.profileEditableUntil) {
        const editableDate = new Date(empData.profileEditableUntil);
        if (now < editableDate) {
          editable = true;
          editDeadline = editableDate;
        }
      }

      if (empData.createdAt) {
        const createdDate = new Date(empData.createdAt);
        const sevenDaysLater = new Date(createdDate);
        sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
        
        if (now < sevenDaysLater) {
          editable = true;
          // Use whichever deadline is later
          if (!editDeadline || sevenDaysLater > editDeadline) {
            editDeadline = sevenDaysLater;
          }
        }
      }

      setCanEdit(editable);
      setEditableUntil(editDeadline);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load profile data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phoneNumbers];
    newPhones[index] = { ...newPhones[index], number: value };
    setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
  };

  const addPhone = () => {
    setFormData(prev => ({ 
      ...prev, 
      phoneNumbers: [...prev.phoneNumbers, { number: '', countryCode: '+91' }] 
    }));
  };

  const removePhone = (index) => {
    const newPhones = formData.phoneNumbers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
  };

  const handleChildChange = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setFormData(prev => ({ ...prev, children: newChildren }));
  };

  const addChild = () => {
    setFormData(prev => ({ 
      ...prev, 
      children: [...prev.children, { name: '', dateOfBirth: '' }] 
    }));
  };

  const removeChild = (index) => {
    const newChildren = formData.children.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, children: newChildren }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      const updateData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'phoneNumbers' || key === 'children') {
          updateData.append(key, JSON.stringify(formData[key]));
        } else {
          updateData.append(key, formData[key]);
        }
      });

      // Append image if selected
      if (imageFile) {
        updateData.append('image', imageFile);
      }

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/employees/${user.user._id}`,
        updateData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Profile updated successfully!'
      });

      setIsEditing(false);
      fetchEmployeeData();
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update profile'
      });
    }
  };

  const toggleDashboardSection = (key) => {
    setDashboardConfig(prev => ({
      ...prev,
      preset: 'custom',
      hidden: prev.hidden.includes(key) ? prev.hidden.filter(k => k !== key) : [...prev.hidden, key],
    }));
  };

  const setSectionWidth = (key, width) => {
    setDashboardConfig(prev => ({ ...prev, preset: 'custom', layout: { ...prev.layout, [key]: width } }));
  };

  const handleReorderDashboard = (newOrder) => {
    setDashboardConfig(prev => ({ ...prev, preset: 'custom', order: normalizeOrder(newOrder) }));
  };

  const toggleChartVisible = (key) => {
    setDashboardConfig(prev => ({
      ...prev,
      preset: 'custom',
      charts: {
        ...prev.charts,
        hidden: prev.charts.hidden.includes(key)
          ? prev.charts.hidden.filter(k => k !== key)
          : [...prev.charts.hidden, key],
      },
    }));
  };

  const setChartWidth = (key, width) => {
    setDashboardConfig(prev => ({
      ...prev,
      preset: 'custom',
      charts: { ...prev.charts, layout: { ...prev.charts.layout, [key]: width } },
    }));
  };

  const handleReorderCharts = (newOrder) => {
    setDashboardConfig(prev => ({
      ...prev,
      preset: 'custom',
      charts: { ...prev.charts, order: normalizeChartOrder(newOrder) },
    }));
  };

  const setDashboardDensity = (density) => {
    setDashboardConfig(prev => ({ ...prev, preset: 'custom', ui: { ...prev.ui, density } }));
  };

  const setDashboardAccent = (accent) => {
    setDashboardConfig(prev => ({ ...prev, preset: 'custom', ui: { ...prev.ui, accent } }));
  };

  const applyPreset = (name) => {
    const preset = applyDashboardPreset(name);
    if (preset) setDashboardConfig(preset);
  };

  const resetDashboard = () => applyPreset('default');

  const handleSaveDashboardPrefs = async () => {
    try {
      setSavingDashboard(true);
      const res = await api.put(
        `${process.env.REACT_APP_API_BASE_URL}/employees/dashboard-preferences/${user.user._id}`,
        {
          hidden: dashboardConfig.hidden,
          order: dashboardConfig.order,
          layout: dashboardConfig.layout,
          ui: dashboardConfig.ui,
          preset: dashboardConfig.preset,
          charts: dashboardConfig.charts,
        }
      );
      const dp = res.data?.dashboardPreferences || {};
      setDashboardConfig({
        hidden: dp.hidden || [],
        order: normalizeOrder(dp.order),
        layout: dp.layout || {},
        ui: { density: dp.ui?.density || 'comfortable', accent: dp.ui?.accent || '' },
        preset: dp.preset || 'custom',
        charts: {
          hidden: dp.charts?.hidden || [],
          order: normalizeChartOrder(dp.charts?.order),
          layout: dp.charts?.layout || {},
        },
      });
      Swal.fire({ icon: 'success', title: 'Dashboard updated', timer: 1500, showConfirmButton: false });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Failed to save dashboard preferences'
      });
    } finally {
      setSavingDashboard(false);
    }
  };

  const handleEmailPDF = async () => {
    // If profile is still editable, ask if user wants to edit first
    if (canEdit) {
      const result = await Swal.fire({
        icon: 'question',
        title: 'Edit Profile First?',
        text: `Your profile is editable until ${editableUntil?.toLocaleDateString()}. Would you like to update your details before sending?`,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Edit Profile',
        denyButtonText: 'Send Anyway',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#7c3aed',
        denyButtonColor: '#2563eb',
      });

      if (result.isConfirmed) {
        setIsEditing(true);
        return;
      }
      if (result.isDismissed) {
        return;
      }
      // result.isDenied means "Send Anyway" — continue below
    }

    try {
      Swal.fire({
        title: 'Generating & Sending PDF...',
        text: 'Please wait while your profile PDF is being generated and emailed.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      // Generate and open PDF in new tab
      const pdfResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/employees/generate-profile-pdf`,
        { employeeId: user.user._id },
        { responseType: 'blob' }
      );

      const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');

      // Also send via email
      const emailResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/employees/email-profile-pdf`,
        { employeeId: user.user._id }
      );

      if (emailResponse.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Profile PDF has been opened and sent to your email. You have 24 hours to update your profile details if needed.',
          confirmButtonColor: '#2563eb'
        });
        // Refresh to update the edit window
        fetchEmployeeData();
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Failed to send PDF to email. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleVerifyEmail = () => {
    // Navigate to verify email page
    navigate('/verify-email');
  };

  const handleResetPassword = () => {
    // Navigate to password reset page
    navigate('/password-reset');
  };

  // ---- WhatsApp Functions ----
  const waHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchWaStatus = async (autoConnect = false) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/employee-whatsapp/status`, waHeaders());
      if (res.data.success) {
        setWaConnected(res.data.data.connected);
        setWaPhone(res.data.data.phone);
        // Auto-connect if not already connected
        if (autoConnect && !res.data.data.connected) {
          handleWaConnect();
        }
      }
    } catch {}
  };

  useEffect(() => {
    if (user) fetchWaStatus(true);
  }, [user]);

  const handleWaConnect = async () => {
    try {
      setWaConnecting(true);
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/employee-whatsapp/connect`, {}, waHeaders());
      if (res.data.data.connected) {
        setWaConnected(true);
        setWaPhone(res.data.data.phone);
        setWaQr(null);
        setWaConnecting(false);
        Swal.fire({ icon: 'success', title: 'Connected!', text: `WhatsApp connected: ${res.data.data.phone}`, timer: 2000 });
        return;
      }
      if (res.data.data.qr) {
        setWaQr(res.data.data.qr);
      }
      setWaPolling(true);
    } catch (error) {
      setWaConnecting(false);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to start WhatsApp connection' });
    }
  };

  // QR polling effect
  useEffect(() => {
    if (!waPolling) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/employee-whatsapp/qr`, waHeaders());
        const data = res.data.data;
        if (data.connected) {
          setWaConnected(true);
          setWaPhone(data.phone);
          setWaQr(null);
          setWaPolling(false);
          setWaConnecting(false);
          Swal.fire({ icon: 'success', title: 'Connected!', text: `WhatsApp connected: ${data.phone}`, timer: 2000 });
        } else if (data.qr) {
          setWaQr(data.qr);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [waPolling]);

  const handleWaDisconnect = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Disconnect WhatsApp?',
      text: 'Your WhatsApp session will be removed.',
      showCancelButton: true,
      confirmButtonText: 'Disconnect',
      confirmButtonColor: '#dc2626'
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/employee-whatsapp/disconnect`, {}, waHeaders());
      setWaConnected(false);
      setWaPhone(null);
      setWaQr(null);
      setSentMessages([]);
      Swal.fire({ icon: 'success', title: 'Disconnected', timer: 1500 });
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to disconnect' });
    }
  };

  const fetchSentMessages = async (page = 1) => {
    try {
      setSentLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/employee-whatsapp/sent-messages?page=${page}&limit=10`, waHeaders());
      if (res.data.success) {
        setSentMessages(res.data.data);
        setSentPagination(res.data.pagination);
        setSentPage(page);
      }
    } catch {
      console.error('Failed to fetch sent messages');
    } finally {
      setSentLoading(false);
    }
  };

  useEffect(() => {
    if (waConnected) fetchSentMessages();
  }, [waConnected]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Employee data not found</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} py-8 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-4xl mx-auto">
        {/* Header Section - Company, Employee, Location */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-lg border p-8 mb-6`}>
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Employee Profile
            </h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Your basic information is displayed below. Complete details are available in the PDF.
            </p>
          </div>

          {/* Employee Image */}
          <div className="mb-8 text-center">
            <div className="inline-block relative">
              {employee.image || employee._id ? (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/employees/image/${employee._id}`}
                  alt="Employee"
                  className="w-32 h-32 rounded-full object-cover border-4 border-blue-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-32 h-32 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center border-4 border-blue-500 mx-auto`}
                   style={{ display: employee.image || employee._id ? 'none' : 'flex' }}>
                <FaUser className={`text-4xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
            </div>
          </div>

          {/* Basic Information Display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 rounded-lg" style={{ background: darkMode ? '#1e3a8a' : '#dbeafe' }}>
              <FaBuilding className={`text-3xl mx-auto mb-3 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} />
              <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>Company</p>
              <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {employee.company?.name || 'N/A'}
              </p>
            </div>
            
            <div className="text-center p-6 rounded-lg" style={{ background: darkMode ? '#065f46' : '#d1fae5' }}>
              <FaUser className={`text-3xl mx-auto mb-3 ${darkMode ? 'text-green-300' : 'text-green-600'}`} />
              <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-green-200' : 'text-green-800'}`}>Employee Name</p>
              <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {employee.name || 'N/A'}
              </p>
            </div>
            
            <div className="text-center p-6 rounded-lg" style={{ background: darkMode ? '#581c87' : '#f3e8ff' }}>
              <FaMapMarkerAlt className={`text-3xl mx-auto mb-3 ${darkMode ? 'text-purple-300' : 'text-purple-600'}`} />
              <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>Location</p>
              <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {employee.assignedLocation?.name || 'N/A'}
              </p>
            </div>
          </div>

          {/* Information Notice */}
          <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
            <div className="flex items-start">
              <FaFilePdf className={`text-2xl mt-1 mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h3 className={`font-semibold mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  Complete Profile Details
                </h3>
                <p className={`text-sm ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  Your complete profile including personal information, family details, documents, and bank information 
                  is available in PDF format. Click "Email PDF" below to receive your full profile in your email inbox.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-lg border p-6`}>
          <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Available Actions
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={handleEmailPDF}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-md"
            >
              <FaEnvelope className="text-lg" /> Email My Profile PDF
            </button>
            
            <button
              onClick={handleVerifyEmail}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium shadow-md"
            >
              <FaCheckCircle className="text-lg" /> Verify Email
            </button>
            
            <button
              onClick={handleResetPassword}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium shadow-md"
            >
              <FaKey className="text-lg" /> Reset Password
            </button>
          </div>

          {canEdit && (
            <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FaEdit className={`text-xl ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <div>
                    <p className={`font-semibold ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                      Profile Editing Available
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      You can update your profile details until {editableUntil?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Dashboard Customization */}
          <div className={`mt-6 p-6 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Dashboard Customization
            </h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Drag to reorder sections, toggle visibility, and set each section's width.
            </p>

            <Reorder.Group axis="y" values={dashboardConfig.order} onReorder={handleReorderDashboard} className="space-y-2">
              {dashboardConfig.order.map(key => {
                const section = DASHBOARD_SECTIONS.find(s => s.key === key);
                const visible = !dashboardConfig.hidden.includes(key);
                const width = dashboardConfig.layout[key] || 'full';
                return (
                  <DashboardSectionRow
                    key={key}
                    sectionKey={key}
                    label={section?.label || key}
                    visible={visible}
                    width={width}
                    darkMode={darkMode}
                    onToggleVisible={toggleDashboardSection}
                    onSetWidth={setSectionWidth}
                  />
                );
              })}
            </Reorder.Group>

            <div className="mt-6">
              <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Charts</h4>
              <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Show/hide, reorder and set each chart to Full or Half width.
              </p>
              <Reorder.Group axis="y" values={dashboardConfig.charts.order} onReorder={handleReorderCharts} className="space-y-2">
                {dashboardConfig.charts.order.map(key => {
                  const chart = DASHBOARD_CHARTS.find(c => c.key === key);
                  const visible = !dashboardConfig.charts.hidden.includes(key);
                  const width = dashboardConfig.charts.layout[key] || chart?.defaultWidth || 'half';
                  return (
                    <DashboardSectionRow
                      key={key}
                      sectionKey={key}
                      label={chart?.label || key}
                      subLabel={CHART_SECTION_LABELS[chart?.section] || ''}
                      visible={visible}
                      width={width}
                      darkMode={darkMode}
                      onToggleVisible={toggleChartVisible}
                      onSetWidth={setChartWidth}
                    />
                  );
                })}
              </Reorder.Group>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Density</label>
                <select
                  value={dashboardConfig.ui.density}
                  onChange={(e) => setDashboardDensity(e.target.value)}
                  className={`w-full p-2 border rounded-lg text-sm ${darkMode ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'}`}
                >
                  <option value="comfortable">Comfortable</option>
                  <option value="compact">Compact</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Accent Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={dashboardConfig.ui.accent || '#6366F1'}
                    onChange={(e) => setDashboardAccent(e.target.value)}
                    className="h-9 w-14 rounded border cursor-pointer"
                  />
                  {dashboardConfig.ui.accent && (
                    <button type="button" onClick={() => setDashboardAccent('')} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Presets:</span>
              <button type="button" onClick={() => applyPreset('default')} className={`px-3 py-1.5 text-xs rounded-lg border ${dashboardConfig.preset === 'default' ? 'bg-indigo-600 text-white border-indigo-600' : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>Default</button>
              <button type="button" onClick={() => applyPreset('compact')} className={`px-3 py-1.5 text-xs rounded-lg border ${dashboardConfig.preset === 'compact' ? 'bg-indigo-600 text-white border-indigo-600' : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>Compact</button>
              <button type="button" onClick={() => applyPreset('focus')} className={`px-3 py-1.5 text-xs rounded-lg border ${dashboardConfig.preset === 'focus' ? 'bg-indigo-600 text-white border-indigo-600' : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>Focus</button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={handleSaveDashboardPrefs} disabled={savingDashboard} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm">
                {savingDashboard ? 'Saving...' : 'Save Dashboard Preferences'}
              </button>
              <button type="button" onClick={resetDashboard} className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                Reset to Default
              </button>
            </div>
          </div>

          {/* WhatsApp Section */}
          <div className={`mt-6 p-6 rounded-lg ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <FaWhatsapp className="text-green-500" /> WhatsApp Reports
            </h3>

            {/* Connection Status */}
            <div className={`p-4 rounded-lg mb-4 ${waConnected ? (darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200') : (darkMode ? 'bg-gray-600 border border-gray-500' : 'bg-white border border-gray-200')}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${waConnected ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <div>
                    <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {waConnected ? 'WhatsApp Connected' : 'WhatsApp Not Connected'}
                    </p>
                    {waPhone && (
                      <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{waPhone}</p>
                    )}
                  </div>
                </div>
                {waConnected ? (
                  <button onClick={handleWaDisconnect} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors">
                    Disconnect
                  </button>
                ) : (
                  <button onClick={handleWaConnect} disabled={waConnecting} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                    {waConnecting ? 'Connecting...' : 'Connect WhatsApp'}
                  </button>
                )}
              </div>
            </div>

            {/* QR Code Display */}
            {waQr && !waConnected && (
              <div className="text-center mb-4">
                <p className={`mb-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Scan this QR code with your WhatsApp app:
                </p>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(waQr)}`}
                  alt="WhatsApp QR Code"
                  className="mx-auto rounded-lg shadow-md"
                  style={{ width: 256, height: 256 }}
                />
                <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Waiting for scan...</p>
              </div>
            )}

            {/* Sent Messages Log */}
            {waConnected && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Messages Sent ({sentPagination.count})
                  </h4>
                  <button onClick={() => fetchSentMessages(sentPage)} className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'} transition-colors`}>
                    Refresh
                  </button>
                </div>
                {sentLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto"></div>
                  </div>
                ) : sentMessages.length === 0 ? (
                  <p className={`text-sm text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No messages sent yet</p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className={`w-full text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <thead>
                          <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                            <th className="px-2 py-2 text-left">To</th>
                            <th className="px-2 py-2 text-left">Type</th>
                            <th className="px-2 py-2 text-left">Via</th>
                            <th className="px-2 py-2 text-left">Status</th>
                            <th className="px-2 py-2 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sentMessages.map((msg) => (
                            <tr key={msg._id} className={`border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                              <td className="px-2 py-2">
                                <div className="font-medium">{msg.recipientName || '-'}</div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{msg.recipientPhone}</div>
                              </td>
                              <td className="px-2 py-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                  msg.automationType === 'tally-quotation-send' ? 'bg-blue-100 text-blue-800' :
                                  msg.automationType === 'delivery-order-send' ? 'bg-purple-100 text-purple-800' :
                                  msg.automationType === 'employee-report' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {msg.automationType === 'tally-quotation-send' ? 'Quotation' :
                                   msg.automationType === 'delivery-order-send' ? 'DO' :
                                   msg.automationType === 'employee-report' ? 'Report' :
                                   msg.automationType}
                                </span>
                              </td>
                              <td className="px-2 py-2">
                                <span className={`text-xs ${msg.sentVia === 'personal' ? 'text-green-500' : 'text-blue-500'}`}>
                                  {msg.sentVia === 'personal' ? 'Personal' : 'Company'}
                                </span>
                              </td>
                              <td className="px-2 py-2">
                                <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
                                  msg.status === 'sent' ? 'bg-green-500' :
                                  msg.status === 'queued' ? 'bg-yellow-500' :
                                  msg.status === 'sending' ? 'bg-blue-500' :
                                  'bg-red-500'
                                }`}></span>
                                {msg.status}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                {new Date(msg.sentAt || msg.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {sentPagination.total > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <button
                          onClick={() => fetchSentMessages(sentPage - 1)}
                          disabled={sentPage <= 1}
                          className={`px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100'} disabled:opacity-50 transition-colors`}
                        >
                          Prev
                        </button>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Page {sentPage} of {sentPagination.total}
                        </span>
                        <button
                          onClick={() => fetchSentMessages(sentPage + 1)}
                          disabled={sentPage >= sentPagination.total}
                          className={`px-2 py-1 text-xs rounded ${darkMode ? 'bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100'} disabled:opacity-50 transition-colors`}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {isEditing && (
            <div className="mt-6">
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Edit Profile Information
                </h3>
                
                {/* Image Upload */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Profile Photo
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {imagePreview || employee._id ? (
                        <img
                          src={imagePreview || `${process.env.REACT_APP_API_BASE_URL}/employees/image/${employee._id}`}
                          alt="Preview"
                          className="w-24 h-24 rounded-full object-cover border-4 border-blue-500"
                        />
                      ) : (
                        <div className={`w-24 h-24 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} flex items-center justify-center border-4 border-blue-500`}>
                          <FaUser className={`text-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                    </div>
                    <label className="cursor-pointer px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                      Choose Photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Personal Information Fields */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Date of Joining
                    </label>
                    <input
                      type="date"
                      name="dateOfJoining"
                      value={formData.dateOfJoining}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Aadhar Number
                    </label>
                    <input
                      type="text"
                      name="aadharNo"
                      value={formData.aadharNo}
                      onChange={handleInputChange}
                      maxLength="12"
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      PAN Number
                    </label>
                    <input
                      type="text"
                      name="panNo"
                      value={formData.panNo}
                      onChange={handleInputChange}
                      maxLength="10"
                      style={{ textTransform: 'uppercase' }}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Father's Name
                    </label>
                    <input
                      type="text"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mother's Name
                    </label>
                    <input
                      type="text"
                      name="motherName"
                      value={formData.motherName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Spouse Name
                    </label>
                    <input
                      type="text"
                      name="spouseName"
                      value={formData.spouseName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      PF Number
                    </label>
                    <input
                      type="text"
                      name="pfNo"
                      value={formData.pfNo}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      ESIC Number
                    </label>
                    <input
                      type="text"
                      name="esicNo"
                      value={formData.esicNo}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Account Name
                    </label>
                    <input
                      type="text"
                      name="accountName"
                      value={formData.accountName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="accountNo"
                      value={formData.accountNo}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      name="ifsc"
                      value={formData.ifsc}
                      onChange={handleInputChange}
                      maxLength="11"
                      style={{ textTransform: 'uppercase' }}
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Postal Address
                    </label>
                    <textarea
                      name="postalAddress"
                      value={formData.postalAddress}
                      onChange={handleInputChange}
                      rows="3"
                      className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>

                  {/* Phone Numbers */}
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Phone Numbers
                    </label>
                    <div className="space-y-2">
                      {formData.phoneNumbers.map((phone, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="tel"
                            value={phone?.number || phone || ''}
                            onChange={(e) => handlePhoneChange(index, e.target.value)}
                            className={`flex-1 px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            placeholder="Enter phone number"
                          />
                          <button
                            onClick={() => removePhone(index)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={addPhone}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        + Add Phone Number
                      </button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Children
                    </label>
                    <div className="space-y-3">
                      {formData.children.map((child, index) => (
                        <div key={index} className={`p-3 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
                          <input
                            type="text"
                            placeholder="Child's Name"
                            value={child.name}
                            onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg mb-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                          />
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={child.dateOfBirth}
                              onChange={(e) => handleChildChange(index, 'dateOfBirth', e.target.value)}
                              className={`flex-1 px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                            />
                            <button
                              onClick={() => removeChild(index)}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={addChild}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        + Add Child
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <FaSave /> Save Changes
                  </button>
                  
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      fetchEmployeeData();
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <FaTimes /> Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
